import React, {useCallback, useMemo, useState} from "react";

/* Utility functions - pure and stable */
function toNumber(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function fmtCurrency(x) {
    if (typeof x !== "number") return x;
    return x.toLocaleString("en-IN", {maximumFractionDigits: 0});
}

function round2(x) {
    return Math.round((Number(x) + Number.EPSILON) * 100) / 100;
}

function computeEmi(principal, annualRate, months) {
    const P = toNumber(principal);
    const n = toNumber(months);
    if (!n || n <= 0) return 0;
    const r = toNumber(annualRate) / 100 / 12;
    if (r === 0) return P / n;
    return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export default function EmiInvestmentCalculator() {
    const [price, setPrice] = useState(100000);
    const [annualReturnPct, setAnnualReturnPct] = useState(18);
    const [withdrawFromInvestment, setWithdrawFromInvestment] = useState(true);
    const [fullCashback, setFullCashback] = useState(347);

    const [plans, setPlans] = useState([{id: 1, months: 3, loanRate: 0, procFee: 299, cashback: 0, emi: 0}, {
        id: 2, months: 6, loanRate: 15.99, procFee: 299, cashback: 0, emi: 0
    }, {id: 3, months: 12, loanRate: 15.99, procFee: 299, cashback: 0, emi: 0},]);

    const [selctedPlanId, setSelectedPlanId] = useState(null);
    const [sortBy, setSortBy] = useState("effectiveCost");
    const [sortDir, setSortDir] = useState("asc");

    const addPlan = useCallback((template = {}) => {
        const id = Date.now();
        setPlans((prev) => [...prev, {
            id,
            months: template.months ?? 6,
            loanRate: template.loanRate ?? 15.99,
            procFee: template.procFee ?? 0,
            cashback: template.cashback ?? 0,
            emi: template.emi ?? 0,
        }]);
        setSelectedPlanId(id);
    }, []);

    const updatePlan = useCallback((id, changes) => {
        setPlans((prev) => prev.map((p) => (p.id === id ? {...p, ...changes} : p)));
    }, []);

    const removePlan = useCallback((id) => {
        setPlans((prev) => prev.filter((p) => p.id !== id));
        setSelectedPlanId((c) => (c === id ? null : c));
    }, []);

    const results = useMemo(() => {
        return plans.map((plan) => {
            const months = toNumber(plan.months, 0);
            const emiRaw = toNumber(plan.emi, 0) > 0 ? toNumber(plan.emi) : computeEmi(price, plan.loanRate || 0, months);
            const emi = round2(emiRaw);
            const totalPaid = round2(emi * months + toNumber(plan.procFee, 0));

            let investmentInterestEarned = 0;
            if (months > 0) {
                // simulate monthly compounding. if withdrawFromInvestment is true,
                // EMI is taken from the balance each month. if false, balance compounds uninterrupted.
                const monthlyRate = toNumber(annualReturnPct) / 100 / 12;
                let balance = toNumber(price);
                for (let i = 0; i < months; i++) {
                    const interest = balance * monthlyRate;
                    investmentInterestEarned += interest;
                    balance += interest;
                    if (withdrawFromInvestment) {
                        balance -= Math.min(balance, emi);
                    }
                    if (balance < 0) balance = 0;
                }
            }

            const effectiveCost = round2(totalPaid - investmentInterestEarned - toNumber(plan.cashback, 0));
            return {
                ...plan,
                months,
                emi,
                totalPaid,
                investmentInterestEarned: round2(investmentInterestEarned),
                effectiveCost,
            };
        });
    }, [plans, price, annualReturnPct, withdrawFromInvestment]);

    const fullPaymentResult = useMemo(() => ({
        id: "full",
        months: 0,
        emi: "Full",
        loanRate: 0,
        procFee: 0,
        cashback: fullCashback,
        totalPaid: toNumber(price),
        investmentInterestEarned: 0,
        effectiveCost: round2(toNumber(price) - toNumber(fullCashback)),
    }), [price, fullCashback]);

    const combined = useMemo(() => {
        const arr = [...results, fullPaymentResult];
        arr.sort((a, b) => {
            const aKey = typeof a[sortBy] === 'number' ? a[sortBy] : (Number(a[sortBy]) || 0);
            const bKey = typeof b[sortBy] === 'number' ? b[sortBy] : (Number(b[sortBy]) || 0);
            if (aKey === bKey) return 0;
            return (aKey < bKey ? -1 : 1) * (sortDir === "asc" ? 1 : -1);
        });
        return arr;
    }, [results, fullPaymentResult, sortBy, sortDir]);

    const best = combined[0] ?? null;

    const exportCsv = useCallback(() => {
        if (!combined.length) return;
        const header = ["Months", "EMI", "LoanRate", "ProcessingFee", "Cashback", "TotalPaid", "InvestmentInterestEarned", "EffectiveCost"].join(",");
        const rows = combined
            .map((r) => [r.months, r.emi, r.loanRate ?? "", r.procFee ?? "", r.cashback ?? "", r.totalPaid, r.investmentInterestEarned, r.effectiveCost].join(","))
            .join("\n");
        const csv = header + "\n" + rows;
        const blob = new Blob([csv], {type: "text/csv"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "emi_investment_comparison.csv";
        a.click();
        URL.revokeObjectURL(url);
    }, [combined]);

    const exportJson = useCallback(() => {
        if (!combined.length) return;
        const blob = new Blob([JSON.stringify(combined, null, 2)], {type: "application/json"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "emi_investment_comparison.json";
        a.click();
        URL.revokeObjectURL(url);
    }, [combined]);

    function planHasErrors(plan) {
        if (plan.id === "full") return null;
        if (toNumber(plan.months) <= 0) return "Months must be > 0";
        if (toNumber(plan.loanRate) < 0) return "Loan rate must not be negative";
        if (toNumber(plan.procFee) < 0) return "Processing fee must not be negative";
        if (toNumber(plan.cashback) < 0) return "Cashback must not be negative";
        return null;
    }

    function handleHeaderSort(key) {
        setSortBy((current) => {
            if (current === key) {
                setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                return current;
            }
            setSortDir("asc");
            return key;
        });
    }

    return (<div className="p-6 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
            <div>
                <h1 className="text-2xl font-semibold">DeptTrap</h1>
                <div className="text-sm text-gray-600">Compare EMI options with investment opportunity cost</div>
            </div>

            <div className="text-sm text-gray-600">
                Best option
                <div className="inline-block ml-2">
                    {best ? (<span
                        className="inline-flex items-center gap-2 px-2 py-1 bg-green-100 text-green-800 rounded text-sm"
                        title="Lowest effective cost">
                <strong>{fmtCurrency(best.effectiveCost)}</strong>
                <span className="text-xs">({best.id === "full" ? "Full pay" : `${best.months} months`})</span>
              </span>) : (<span>-</span>)}
                </div>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 border rounded space-y-3 bg-white">
                <div>
                    <label className="text-xs text-gray-600">Item price</label>
                    <input
                        className="w-full mt-1 p-2 border rounded"
                        type="number"
                        min="0"
                        value={price}
                        onChange={(e) => setPrice(Math.max(0, toNumber(e.target.value, 0)))}
                        aria-label="Item price"
                        title="Total price to consider investing or paying"
                    />
                </div>

                <div>
                    <label className="text-xs text-gray-600">Expected annual return percentage</label>
                    <input
                        className="w-full mt-1 p-2 border rounded"
                        type="number"
                        min="0"
                        step="0.1"
                        value={annualReturnPct}
                        onChange={(e) => setAnnualReturnPct(Math.max(0, toNumber(e.target.value, 0)))}
                        aria-label="Expected annual return percent"
                    />
                    <div className="text-xs text-gray-500 mt-1">Assumed annual return used for balance simulation.
                    </div>
                </div>

                <div className="relative">
                    <label className="text-xs text-gray-600 flex items-center gap-2">
                        Withdraw EMIs from investment
                        <div className="relative group">
      <span
          className="inline-flex items-center justify-center w-4 h-4 text-xs font-bold border rounded-full cursor-pointer text-gray-600 border-gray-400"
      >
        i
      </span>

                            {/* Tooltip */}
                            <div
                                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-64 bg-gray-800 text-white text-xs rounded p-2 shadow-lg opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-opacity duration-200 z-50"
                            >
                                <p>
                                    <strong>Yes</strong>: EMIs are withdrawn from your invested amount.
                                    The investment earns monthly returns but balance decreases with each EMI.
                                </p>
                                <p className="mt-1">
                                    <strong>No</strong>: EMIs are paid from your pocket. Investment remains untouched
                                    and compounds monthly.
                                </p>
                            </div>
                        </div>
                    </label>

                    <select
                        className="w-full mt-1 p-2 border rounded"
                        value={withdrawFromInvestment ? "yes" : "no"}
                        onChange={(e) => setWithdrawFromInvestment(e.target.value === "yes")}
                        aria-label="Withdraw EMIs from investment"
                    >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                </div>

                <div className="mt-4 flex gap-2">
                    <button onClick={exportCsv} className="px-3 py-2 bg-blue-600 text-white rounded"
                            title="Export CSV" disabled={!combined.length}>Export CSV
                    </button>
                    <button onClick={exportJson} className="px-3 py-2 bg-gray-800 text-white rounded"
                            title="Export JSON" disabled={!combined.length}>Export JSON
                    </button>
                </div>

            </div>

            <div className="md:col-span-3 space-y-4">
                <div className="p-4 border rounded bg-white">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="Add months"
                                        className="w-22 p-1 border rounded text-sm"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const v = Math.max(1, toNumber(e.target.value));
                                                if (v) addPlan({months: v, loanRate: 10});
                                                e.currentTarget.value = '';
                                            }
                                        }}
                                        title="Type months and press Enter to add custom tenure"
                                    />
                                    <button className="px-2 py-1.5 text-xs border rounded" onClick={(e) => {
                                        const input = e.currentTarget.previousElementSibling;
                                        const v = input ? Math.max(1, toNumber(input.value)) : 0;
                                        if (v) {
                                            addPlan({months: v, loanRate: 10});
                                            input.value = '';
                                        }
                                    }} title="Add custom tenure">Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="text-left text-gray-500 sticky top-0 bg-white">
                            <tr>
                                {/* First header cell is sticky to the left so the Months column stays visible */}
                                <th className="p-2 sticky left-0 z-30 bg-white">Months</th>

                                <th className="p-2">EMI</th>
                                <th className="p-2">Loan %</th>
                                <th className="p-2">Processing fee</th>
                                <th className="p-2">Cashback</th>
                                <th className="p-2 cursor-pointer" onClick={() => handleHeaderSort('totalPaid')}
                                    aria-label="Sort by total paid">Total
                                    paid {sortBy === 'totalPaid' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                <th className="p-2 cursor-pointer"
                                    onClick={() => handleHeaderSort('investmentInterestEarned')}
                                    aria-label="Sort by investment interest">Investment
                                    interest {sortBy === 'investmentInterestEarned' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                <th className="p-2 cursor-pointer" onClick={() => handleHeaderSort('effectiveCost')}
                                    aria-label="Sort by effective cost">Effective
                                    cost {sortBy === 'effectiveCost' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                                <th className="p-2"/>
                            </tr>
                            </thead>
                            <tbody>
                            {combined.map((r) => {
                                const isBest = best && r.id === best.id;
                                const error = r.id !== "full" ? planHasErrors(r) : null;

                                // numeric-safe savings vs full payment
                                const fullEff = Number.isFinite(fullPaymentResult.effectiveCost) ? fullPaymentResult.effectiveCost : toNumber(price);
                                const rEff = Number.isFinite(r.effectiveCost) ? r.effectiveCost : toNumber(price);
                                const savingsVsFull = round2(fullEff - rEff);

                                // keep first column sticky. use higher z for header, lower for rows so header sits above
                                return (<tr key={r.id}
                                            className={`${isBest ? "bg-green-50 font-semibold" : ""} ${error ? "opacity-80" : ""} hover:bg-gray-50`}
                                            title={r.id === 'full' ? 'Full payment' : `${r.months} months`}>
                                    <td className="p-2 sticky left-0 z-20 bg-white">
                                        {r.id === "full" ? (<span>Full</span>) : (<input
                                            className="w-20 p-1 border rounded text-sm"
                                            value={r.months}
                                            onChange={(e) => updatePlan(r.id, {months: Math.max(0, toNumber(e.target.value))})}
                                            title="Months"
                                            aria-label={`Months for plan ${r.id}`}
                                        />)}
                                    </td>

                                    <td className="p-2">{r.id === "full" ? (r.emi) : (<input
                                        className="w-20 p-1 border rounded text-sm"
                                        value={r.emi}
                                        onChange={(e) => updatePlan(r.id, {emi: Math.max(0, toNumber(e.target.value))})}
                                        placeholder={`Auto ${fmtCurrency(computeEmi(price, r.loanRate || 0, r.months))}`}
                                        title="EMI amount. Leave zero to auto compute"
                                    />)}</td>

                                    <td className="p-2">{r.id === "full" ? ("-") : (<input
                                        className="w-20 p-1 border rounded text-sm"
                                        value={r.loanRate}
                                        onChange={(e) => updatePlan(r.id, {loanRate: Math.max(0, toNumber(e.target.value))})}
                                        title="Annual loan percent"
                                    />)}</td>

                                    <td className="p-2"><input className="w-24 p-1 border rounded text-sm"
                                                               value={r.procFee ?? 0}
                                                               onChange={(e) => {
                                                                   if (r.id === 'full') return;
                                                                   updatePlan(r.id, {procFee: Math.max(0, toNumber(e.target.value))});
                                                               }} title="Processing fee"/></td>

                                    <td className="p-2">{r.id === "full" ? (
                                        <input className="w-20 p-1 border rounded text-sm"
                                               value={fullCashback}
                                               onChange={(e) => setFullCashback(Math.max(0, toNumber(e.target.value)))}
                                               title="Cashback for full payment"
                                               aria-label="Full payment cashback"/>) : (
                                        <input className="w-20 p-1 border rounded text-sm"
                                               value={r.cashback ?? 0}
                                               onChange={(e) => updatePlan(r.id, {cashback: Math.max(0, toNumber(e.target.value))})}
                                               title="Cashback"/>)}</td>

                                    <td className="p-2 whitespace-nowrap">{fmtCurrency(r.totalPaid)}</td>
                                    <td className="p-2">{fmtCurrency(r.investmentInterestEarned)}</td>

                                    <td className="p-2 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <span>{fmtCurrency(r.effectiveCost)}</span>
                                            {(savingsVsFull > 0 && (<span
                                                className="text-xs text-yellow-700 font-medium">Save {fmtCurrency(savingsVsFull)}</span>))}
                                            {(savingsVsFull < 0 && (<span
                                                className="text-xs text-red-700 font-medium">Loss {(fmtCurrency(savingsVsFull).replace(/-/g, ''))}</span>))}
                                        </div>
                                    </td>

                                    <td className="p-2">
                                        <div className="flex gap-1">
                                            {r.id !== "full" && (<>
                                                <button className="px-2 py-1 text-xs border rounded"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removePlan(r.id);
                                                        }} title="Remove plan">Remove
                                                </button>
                                            </>)}
                                        </div>
                                        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
                                    </td>
                                </tr>);
                            })}
                            </tbody>
                        </table>
                    </div>

                </div>
            </div>
        </div>
    </div>);
}
